import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock4, Users, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const RO_WEEKDAYS = ["duminică", "luni", "marți", "miercuri", "joi", "vineri", "sâmbătă"];
const RO_MONTHS = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
  "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];

function formatWebinarDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${RO_WEEKDAYS[d.getDay()]}, ${d.getDate()} ${RO_MONTHS[d.getMonth()]}`;
}

/**
 * Homepage "Next webinar" section. Auto-fetches the soonest published
 * webinar and renders a CTA to `/webinarii`. Silently renders
 * `null` when there are no upcoming webinars — the homepage flow is not
 * disrupted by an empty panel.
 */
export default function NextWebinar() {
  const [webinar, setWebinar] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/webinars/public")
      .then((r) => {
        if (cancelled) return;
        const first = (r.data?.webinars || [])[0] || null;
        setWebinar(first);
      })
      .catch(() => {
        if (!cancelled) setWebinar(null);
      });
    return () => { cancelled = true; };
  }, []);

  if (!webinar) return null;

  const seats =
    webinar.capacity > 0 ? `${webinar.seats_remaining} / ${webinar.capacity} locuri` : "Locuri limitate";

  return (
    <section
      className="bg-[#F0E7F4]/40 border-y border-[#E5E7EB]"
      data-testid="homepage-next-webinar"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest border border-[#965EA7]/20">
          <Sparkles size={12} /> Participă la următorul webinar gratuit
        </div>
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#373A45] leading-tight">
              {webinar.title}
            </h2>
            {webinar.short_description && (
              <p className="mt-3 text-[#4b4f5a] leading-relaxed max-w-xl">
                {webinar.short_description}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#373A45]">
              <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} className="text-[#965EA7]" /> {formatWebinarDate(webinar.date)}</span>
              <span className="inline-flex items-center gap-1.5"><Clock4 size={14} className="text-[#965EA7]" /> {webinar.start_time}</span>
              <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-[#965EA7]" /> {seats}</span>
            </div>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/webinarii"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-7 py-3.5 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all"
              data-testid="homepage-next-webinar-cta"
            >
              Vezi detalii webinar <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
